import api from "../utils/axios";

// Fetch subscription + org data from backend
export const getUserSubscription = async () => {
  try {
    const response = await api.get("/api/user/subscription");
    if (response.data.success) {
      return {
        ...response.data.subscription,
        org: response.data.org || null,
      };
    } else {
      throw new Error(response.data.error || "Failed to get subscription status");
    }
  } catch (error) {
    console.error("Failed to get subscription status:", error);
    throw error;
  }
};

// Plan definitions — EQ Scout pricing
const PLANS = {
  free: {
    name: "Free",
    monthlySearches: 25,
    monthlyReveals: 0,
    trialReveals: 0,
    seats: 1,
    canUseFeatures: true,
    color: "gray",
    description: "Search for free, pay for reveals",
  },
  starter: {
    name: "Starter",
    price: 49,
    monthlySearches: -1,
    monthlyReveals: 40,
    seats: 1,
    canUseFeatures: true,
    color: "blue",
    description: "The Sales Navigator Killer. 40 credits/month.",
  },
  growth: {
    name: "Growth",
    price: 149,
    monthlySearches: -1,
    monthlyReveals: 200,
    seats: 5,
    canUseFeatures: true,
    color: "purple",
    description: "The Core BD Engine. 200 credits/month.",
  },
  scale: {
    name: "Scale",
    price: 499,
    monthlySearches: -1,
    monthlyReveals: 750,
    seats: 15,
    canUseFeatures: true,
    color: "blue",
    description: "AI Transformation at Scale. 750 credits/month.",
  },
  trial: {
    name: "Trial",
    monthlySearches: -1,
    monthlyReveals: 0,
    trialReveals: 50,
    seats: 5,
    canUseFeatures: true,
    color: "amber",
    description: "Full access trial with 50 reveal credits",
  },
  enterprise: {
    name: "Enterprise",
    monthlySearches: -1,
    monthlyReveals: 0,
    seats: null,
    canUseFeatures: true,
    color: "gold",
    description: "Custom plan with dedicated support",
  },
  // Legacy aliases
  individual: {
    name: "Starter",
    price: 49,
    monthlySearches: -1,
    monthlyReveals: 40,
    seats: 1,
    canUseFeatures: true,
    color: "blue",
    description: "The Sales Navigator Killer. 40 credits/month.",
  },
  team: {
    name: "Growth",
    price: 149,
    monthlySearches: -1,
    monthlyReveals: 200,
    seats: 5,
    canUseFeatures: true,
    color: "purple",
    description: "The Core BD Engine. 200 credits/month.",
  },
};

export const getPlanInfo = (subscriptionOrPlanType) => {
  // Handle both legacy status strings and new org planType
  const key = String(subscriptionOrPlanType || "free").toLowerCase();

  // Legacy mapping
  const legacyMap = {
    basic: "starter",
    launch: "starter",
    pro: "starter",
    individual: "starter",
    team: "growth",
    partner: "growth",
  };

  const planKey = legacyMap[key] || key;
  return PLANS[planKey] || PLANS.free;
};

export const checkSubscriptionAccess = (subscriptionStatus) => {
  const status = String(subscriptionStatus || "").toLowerCase();
  const accessPlans = ["free", "starter", "growth", "scale", "trial", "enterprise",
    "basic", "pro", "individual", "team", "launch", "partner"];
  return accessPlans.includes(status);
};

// Start a checkout session for Individual or Team plan
export const startCheckout = async ({ planKey = "individual" } = {}) => {
  const { data } = await api.post("/api/billing/checkout", { planKey });
  if (data?.url) {
    window.location.assign(data.url);
  } else {
    throw new Error("Failed to start checkout: no URL returned");
  }
};

// Start a checkout for credit top-up
export const startCreditPurchase = async (quantity) => {
  const { data } = await api.post("/api/billing/checkout-credits", { quantity });
  if (data?.url) {
    window.location.assign(data.url);
  } else {
    throw new Error("Failed to start credit purchase: no URL returned");
  }
};

// Fetch credit tier pricing
export const getCreditPricing = async () => {
  const { data } = await api.get("/api/billing/credit-pricing");
  return data;
};

// Organization API helpers
export const getOrg = async () => {
  const { data } = await api.get("/api/org");
  return data.org;
};

export const getOrgMembers = async () => {
  const { data } = await api.get("/api/org/members");
  return data.members;
};

export const getOrgInvitations = async () => {
  const { data } = await api.get("/api/org/invitations");
  return data.invitations;
};

export const revokeInvitation = async (invitationId) => {
  const { data } = await api.delete(`/api/org/invitations/${invitationId}`);
  return data;
};

export const resendInvitation = async (invitationId) => {
  const { data } = await api.post(`/api/org/invite/${invitationId}/resend`);
  return data;
};

export const inviteMember = async (email, role = "member") => {
  const { data } = await api.post("/api/org/invite", { email, role });
  return data;
};

export const removeMember = async (userId) => {
  const { data } = await api.delete(`/api/org/members/${userId}`);
  return data;
};

export const updateMemberRole = async (userId, role) => {
  const { data } = await api.patch(`/api/org/members/${userId}/role`, { role });
  return data;
};

export const acceptInvitation = async (token) => {
  const { data } = await api.post("/api/org/invite/accept", { token });
  return data;
};

export const getOrgUsage = async () => {
  const { data } = await api.get("/api/org/usage");
  return data;
};

export const updateOrgSettings = async (updates) => {
  const { data } = await api.patch("/api/org", updates);
  return data;
};

// Legacy — kept for backward compat
export const getPaymentLinks = () => ({});
